import {
  companyUsesCcpV2,
  loadCycleAndRedirectIfArchived,
  redirectIfInactiveGroup,
  redirectToHomeIfNoCcpAccess,
  redirectToHomeIfNotAdmin,
  redirectToHomeIfNotPermission,
  redirectToNotFoundIfNoWaveId,
} from '@/ccp-mfe/router/guards';

const LayoutCcpMfe = () => import('@/ccp-mfe/layouts/layout-ccp-mfe.vue');
const CcpListPage = () => import('@/ccp-mfe/pages/ccp-list.vue');
const CcpDashboardPage = () => import('@/ccp-mfe/pages/ccp-dashboard.vue');
const CcpDetailsPage = () => import('@/ccp-mfe/pages/ccp-details.vue');
const CcpSettingsPage = () => import('@/ccp-mfe/pages/ccp-settings.vue');
const CcpSettingsGeneralPage = () => import('@/ccp-mfe/pages/ccp-settings-general.vue');
const CcpSettingsImportPage = () => import('@/ccp-mfe/pages/ccp-settings-import.vue');
const CcpSettingsExportPage = () => import('@/ccp-mfe/pages/ccp-settings-export.vue');
const CcpSettingsRewardLetterTemplatesPage = () => import('@/ccp-mfe/pages/ccp-settings-reward-letter-templates.vue');
const CcpSettingsSentRewardLettersPage = () => import('@/ccp-mfe/pages/ccp-settings-sent-reward-letters.vue');
const CcpSettingsPlanningHierarchyPage = () => import('@/ccp-mfe/pages/ccp-settings-planning-hierarchy.vue');
const CcpSettingsPlanningHierarchyWavePage = () => import('@/ccp-mfe/pages/ccp-settings-planning-hierarchy-wave.vue');
const CcpNotFoundPage = () => import('@/ccp-mfe/pages/ccp-not-found.vue');
const CcpDownloadFilePage = () => import('@/ccp-mfe/pages/ccp-download-file.vue');
const CcpColumnsConfigPage = () => import('@/ccp-mfe/pages/ccp-columns-config.vue');
const CcpColumnsConfigComputationDrawer = () =>
  import('@/ccp-mfe/components/ccp-columns-config/computation-drawer/computation-drawer.vue');
const CcpSettingsRolesPage = () => import('@/ccp-mfe/pages/ccp-settings-roles.vue');
const CcpSettingsNotificationsPage = () => import('@/ccp-mfe/pages/ccp-settings-notifications.vue');
const CcpSettingsHrisDashboardPage = () => import('@/ccp-mfe/pages/ccp-settings-hris-dashboard.vue');
const CcpSettingsExchangeRatesPage = () => import('@/ccp-mfe/pages/ccp-settings-exchange-rates.vue');
const CcpSettingsCompensationElementsPage = () => import('@/ccp-mfe/pages/ccp-settings-compensation-elements.vue');

const meta = {
  layout: 'layout-default',
};

export const ccpMfeRoutes = [
  {
    path: '',
    component: LayoutCcpMfe,
    meta,
    children: [
      {
        path: '/ccp-mfe-list',
        name: 'ccp-mfe-list',
        component: CcpListPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNoCcpAccess],
      },
      {
        path: '/ccp-mfe-dashboard/:cycleId',
        name: 'ccp-mfe-dashboard',
        component: CcpDashboardPage,
        beforeEnter: [redirectToHomeIfNotAdmin, companyUsesCcpV2],
      },
      {
        path: '/ccp-mfe-details/:cycleId/admin',
        name: 'ccp-mfe-admin-details',
        component: CcpDetailsPage,
        beforeEnter: [redirectToHomeIfNotAdmin, companyUsesCcpV2, loadCycleAndRedirectIfArchived],
      },
      {
        path: '/ccp-mfe-details/:cycleId/group/:groupId/role/:roleId',
        name: 'ccp-mfe-group-details',
        component: CcpDetailsPage,
        beforeEnter: [companyUsesCcpV2, loadCycleAndRedirectIfArchived, redirectIfInactiveGroup],
      },
      {
        path: '/ccp-mfe-columns-config/:cycleId',
        name: 'ccp-mfe-columns-config',
        component: CcpColumnsConfigPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNotAdmin],
        children: [
          {
            path: 'computation/:columnName/:status?',
            name: 'ccp-mfe-columns-config-computation',
            component: CcpColumnsConfigComputationDrawer,
            props: true,
          },
        ],
      },
      // Settings pages for Admins
      {
        path: '/ccp-mfe-settings/:cycleId',
        redirect: { name: 'ccp-mfe-settings-general' },
        name: 'ccp-mfe-settings',
        component: CcpSettingsPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNotAdmin],
        children: [
          {
            path: 'general',
            name: 'ccp-mfe-settings-general',
            component: CcpSettingsGeneralPage,
          },
          {
            path: 'compensation-elements',
            name: 'ccp-mfe-settings-compensation-elements',
            component: CcpSettingsCompensationElementsPage,
          },
          {
            path: 'import',
            name: 'ccp-mfe-settings-import',
            component: CcpSettingsImportPage,
          },
          {
            path: 'export',
            name: 'ccp-mfe-settings-export',
            component: CcpSettingsExportPage,
          },
          {
            path: 'reward-letter-templates',
            name: 'ccp-mfe-settings-reward-letter-templates',
            component: CcpSettingsRewardLetterTemplatesPage,
          },
          {
            path: 'sent-reward-letters',
            name: 'ccp-mfe-settings-sent-reward-letters',
            component: CcpSettingsSentRewardLettersPage,
          },
          {
            path: 'planning-hierarchy',
            name: 'ccp-mfe-settings-planning-hierarchy',
            component: CcpSettingsPlanningHierarchyPage,
            beforeEnter: [redirectToNotFoundIfNoWaveId],
            children: [
              {
                path: ':waveId',
                name: 'ccp-mfe-settings-planning-hierarchy-wave',
                component: CcpSettingsPlanningHierarchyWavePage,
              },
            ],
          },
          {
            path: 'roles',
            name: 'ccp-mfe-settings-roles',
            component: CcpSettingsRolesPage,
          },
          {
            path: 'notifications',
            name: 'ccp-mfe-settings-notifications',
            component: CcpSettingsNotificationsPage,
          },
          {
            path: 'exchange-rates',
            name: 'ccp-mfe-settings-exchange-rates',
            component: CcpSettingsExchangeRatesPage,
          },
          {
            path: 'hris-dashboard',
            name: 'ccp-mfe-settings-hris-dashboard',
            component: CcpSettingsHrisDashboardPage,
          },
        ],
      },
      // Settings pages for Non-Admins
      {
        path: '/ccp-mfe-group-settings/:cycleId/group/:groupId/role/:roleId',
        redirect: { name: 'ccp-mfe-group-settings-export' },
        name: 'ccp-mfe-group-settings',
        component: CcpSettingsPage,
        beforeEnter: [companyUsesCcpV2, redirectToHomeIfNotPermission('can_export_data')],
        children: [
          {
            path: 'export',
            name: 'ccp-mfe-group-settings-export',
            component: CcpSettingsExportPage,
          },
        ],
      },
    ],
  },
  // Routes that don't need CCP header
  {
    path: '/ccp-mfe-download-file',
    name: 'ccp-mfe-download-file',
    component: CcpDownloadFilePage,
    beforeEnter: [companyUsesCcpV2],
    meta,
  },
  {
    path: '/ccp-mfe-not-found',
    name: 'ccp-mfe-only-404',
    component: CcpNotFoundPage,
    meta,
  },
];
